import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  Trophy,
  TrendingDown,
  TrendingUp,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  getAttendanceReport,
  getReportingScopes,
  type ReportingScope,
  type AttendanceHighlight,
} from "@/api/reporting";
import { AnimatedNumber } from "@/components/ui/AnimatedNumber";
import { Card } from "@/components/ui/card";
import { LoadingPage } from "@/components/ui/LoadingPage";
import { PageContainer } from "@/design-system";
import { useAuth } from "@/features/auth/hooks/useAuth";

const periodOptions = [
  { label: "Last 30 Days", value: 30 },
  { label: "Last 60 Days", value: 60 },
  { label: "Last 90 Days", value: 90 },
  { label: "Last 6 Months", value: 180 },
  { label: "Last Year", value: 365 },
];

export function AttendanceReportPage() {
  const { churchId } = useAuth();

  const [scopeValue, setScopeValue] = useState("overall");
  const scope: ReportingScope =
    scopeValue === "overall" || scopeValue === "main" ? scopeValue : "ministry";
  const ministryId = scope === "ministry" ? scopeValue : undefined;
  const scopesQuery = useQuery({
    queryKey: ["reporting-scopes", churchId],
    enabled: !!churchId,
    queryFn: () => getReportingScopes(churchId!),
  });

  const [days, setDays] = useState(90);
  const [serviceType, setServiceType] = useState("");

  const reportQuery = useQuery({
    queryKey: [
      "attendance-report",
      churchId,
      days,
      serviceType,
      scopeValue,
    ],
    enabled: !!churchId,
    queryFn: () =>
      getAttendanceReport(
        churchId!,
        days,
        serviceType || undefined,
        scope,
        ministryId
      ),
  });

  if (reportQuery.isLoading) {
    return (
      <PageContainer>
        <LoadingPage />
      </PageContainer>
    );
  }

  if (
    reportQuery.isError ||
    !reportQuery.data
  ) {
    return (
      <PageContainer>
        <div className="rounded-2xl border border-rose-500/20 bg-rose-500/5 p-6 text-rose-300">
          Unable to load attendance report.
        </div>
      </PageContainer>
    );
  }

  const report = reportQuery.data;

  return (
    <PageContainer>
      <div className="flex items-start gap-4">
        <Link
          to="/reports"
          className="mt-1 flex h-10 w-10 items-center justify-center rounded-xl border border-slate-800 bg-slate-900 text-slate-400 transition hover:text-white"
        >
          <ArrowLeft size={18} />
        </Link>

        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-indigo-400">
            Reports
          </p>

          <h1 className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">
            Attendance Report
          </h1>

          <p className="mt-2 text-base text-slate-400">
            Analyze service attendance and trends over time.
          </p>
        </div>
      </div>

      <div className="mt-7 flex flex-col gap-3 rounded-2xl border border-slate-800 bg-slate-900/70 p-4 md:flex-row md:items-center">
        <select
          aria-label="Reporting scope"
          value={scopeValue}
          onChange={event => {
            setScopeValue(event.target.value);
            setServiceType("");
          }}
          className="rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm text-white"
        >
          <option value="overall">Overall Church</option>
          <option value="main">Main Church</option>
          {(scopesQuery.data ?? []).map(ministry => (
            <option key={ministry.ministry_id} value={ministry.ministry_id}>
              {ministry.name}{ministry.is_active ? "" : " (Archived)"}
            </option>
          ))}
        </select>
        {scopesQuery.isError && <p role="status">Ministry filters could not be loaded.</p>}
        <select
          value={days}
          onChange={(event) =>
            setDays(Number(event.target.value))
          }
          className="rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm text-white"
        >
          {periodOptions.map((option) => (
            <option
              key={option.value}
              value={option.value}
            >
              {option.label}
            </option>
          ))}
        </select>

        <select
          value={serviceType}
          onChange={(event) =>
            setServiceType(event.target.value)
          }
          className="rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm text-white"
        >
          <option value="">
            All Service Types
          </option>

          {report.available_service_types.map(
            (type) => (
              <option
                key={type}
                value={type}
              >
                {formatServiceType(type)}
              </option>
            )
          )}
        </select>

        <p className="text-sm text-slate-500 md:ml-auto">
          {formatDate(report.period_start)} -{" "}
          {formatDate(report.period_end)}
        </p>
      </div>

      <div className="mt-7 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          icon={<CalendarDays size={23} />}
          label="Eligible Services"
          value={report.eligible_services}
          description="Past services not cancelled"
        />

        <MetricCard
          icon={<CheckCircle2 size={23} />}
          label="Total Present Check-ins"
          value={report.total_present}
          description="Total PRESENT attendance"
        />

        <MetricCard
          icon={<Users size={23} />}
          label="Avg Attendance Per Service"
          value={report.average_attendance}
          decimals={1}
          description="Present check-ins per eligible service"
        />

        <ChangeMetric
          value={report.attendance_change}
        />
      </div>

      <p className="mt-4 text-sm text-slate-400">Unique attendees: {report.unique_attendees}</p>

      <div className="mt-7">
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-white">
            Attendance Trend
          </h2>

          <p className="mt-1 text-sm text-slate-500">
            Present check-ins by eligible service date
          </p>

          <div className="mt-6 h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={report.attendance_trend}
                margin={{
                  top: 12,
                  right: 12,
                  left: -18,
                  bottom: 0,
                }}
              >
                <defs>
                  <linearGradient
                    id="attendanceReportGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="5%"
                      stopColor="#818cf8"
                      stopOpacity={0.45}
                    />

                    <stop
                      offset="95%"
                      stopColor="#818cf8"
                      stopOpacity={0.02}
                    />
                  </linearGradient>
                </defs>

                <CartesianGrid
                  stroke="#1e293b"
                  strokeDasharray="4 4"
                  vertical={false}
                />

                <XAxis
                  dataKey="label"
                  stroke="#64748b"
                  tickLine={false}
                  axisLine={false}
                />

                <YAxis
                  stroke="#64748b"
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />

                <Tooltip
                  contentStyle={{
                    background: "#020617",
                    border: "1px solid #1e293b",
                    borderRadius: "14px",
                    color: "#fff",
                  }}
                />

                <Area
                  type="monotone"
                  dataKey="present"
                  name="Present"
                  stroke="#818cf8"
                  strokeWidth={3}
                  fill="url(#attendanceReportGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div className="mt-7 grid gap-6 xl:grid-cols-[0.7fr_1.8fr]">
        <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-1">
          <HighlightCard
            title="Highest Attendance"
            highlight={report.highest_attendance}
            variant="HIGH"
          />

          <HighlightCard
            title="Lowest Attendance"
            highlight={report.lowest_attendance}
            variant="LOW"
          />
        </div>

        <Card className="overflow-hidden">
          <div className="border-b border-slate-800 px-6 py-5">
            <h2 className="text-xl font-semibold text-white">
              Service Attendance Details
            </h2>

            <p className="mt-1 text-sm text-slate-500">
              Compare each service with the period average
              and previous eligible service.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-950/60 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-6 py-4">
                    Service
                  </th>

                  <th className="px-6 py-4">
                    Date
                  </th>

                  <th className="px-6 py-4">
                    Type
                  </th>

                  <th className="px-6 py-4 text-right">
                    Present
                  </th>

                  <th className="px-6 py-4 text-right">
                    vs Average
                  </th>

                  <th className="px-6 py-4 text-right">
                    vs Prior Service
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-800">
                {report.services.map((service) => (
                  <tr
                    key={service.service_occurrence_id}
                    className="transition hover:bg-slate-800/30"
                  >
                    <td className="px-6 py-4 font-semibold text-white">
                      {service.service_title}
                    </td>

                    <td className="px-6 py-4 text-slate-400">
                      {formatDate(service.starts_at)}
                    </td>

                    <td className="px-6 py-4 text-slate-400">
                      {formatServiceType(
                        service.service_type
                      )}
                    </td>

                    <td className="px-6 py-4 text-right font-semibold text-white">
                      {service.present}
                    </td>

                    <ChangeCell
                      value={service.vs_average}
                    />

                    <ChangeCell
                      value={
                        service.vs_previous_service
                      }
                    />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <div className="mt-7 rounded-xl border border-indigo-500/15 bg-indigo-500/5 px-5 py-4 text-sm text-slate-400">
        Only past service occurrences that are not marked
        CANCELLED are included. Attendance counts use PRESENT
        records tied to those occurrences.
      </div>
    </PageContainer>
  );
}

function MetricCard({
  icon,
  label,
  value,
  description,
  decimals = 0,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  description: string;
  decimals?: number;
}) {
  return (
    <Card className="p-6">
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-500/15 text-indigo-400">
        {icon}
      </div>

      <p className="mt-5 text-sm font-medium text-slate-400">
        {label}
      </p>

      <AnimatedNumber
        value={value}
        decimals={decimals}
        className="mt-2 block text-4xl font-bold text-white"
      />

      <p className="mt-3 text-xs text-slate-600">
        {description}
      </p>
    </Card>
  );
}

function ChangeMetric({
  value,
}: {
  value: number | null;
}) {
  const negative = value !== null && value < 0;
  const Icon = negative ? TrendingDown : TrendingUp;

  return (
    <Card className="p-6">
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-400">
        <Icon size={23} />
      </div>

      <p className="mt-5 text-sm font-medium text-slate-400">
        Change vs Prior Period
      </p>

      <p className="mt-2 text-4xl font-bold text-white">
        {formatChange(value)}
      </p>

      <p className="mt-3 text-xs text-slate-600">
        Compared with previous equal period
      </p>
    </Card>
  );
}

function HighlightCard({
  title,
  highlight,
  variant,
}: {
  title: string;
  highlight: AttendanceHighlight | null;
  variant: "HIGH" | "LOW";
}) {
  const positive = variant === "HIGH";

  return (
    <Card className="p-6">
      <p
        className={
          positive
            ? "font-semibold text-emerald-400"
            : "font-semibold text-rose-400"
        }
      >
        {title}
      </p>

      {!highlight ? (
        <p className="mt-6 text-sm text-slate-500">
          No attendance data available.
        </p>
      ) : (
        <>
          <div
            className={`mt-6 flex h-12 w-12 items-center justify-center rounded-xl ${
              positive
                ? "bg-emerald-500/15 text-emerald-400"
                : "bg-rose-500/15 text-rose-400"
            }`}
          >
            {positive ? (
              <Trophy size={23} />
            ) : (
              <TrendingDown size={23} />
            )}
          </div>

          <p className="mt-5 text-sm text-slate-400">
            {formatDate(highlight.starts_at)} -{" "}
            {highlight.service_title}
          </p>

          <p className="mt-5 text-4xl font-bold text-white">
            {highlight.present}
            <span className="ml-2 text-sm font-medium text-slate-500">
              present
            </span>
          </p>

          <p className="mt-3 text-sm text-slate-500">
            {formatChange(highlight.vs_average)} vs average
          </p>
        </>
      )}
    </Card>
  );
}

function ChangeCell({
  value,
}: {
  value: number | null;
}) {
  const className =
    value === null
      ? "text-slate-600"
      : value > 0
        ? "text-emerald-400"
        : value < 0
          ? "text-rose-400"
          : "text-slate-400";

  return (
    <td
      className={`px-6 py-4 text-right font-medium ${className}`}
    >
      {formatChange(value)}
    </td>
  );
}

function formatChange(value: number | null) {
  if (value === null) {
    return "-";
  }

  return `${value > 0 ? "+" : ""}${value}%`;
}

function formatServiceType(value: string) {
  return value
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) =>
      letter.toUpperCase()
    );
}

function formatDate(value: string) {
  const date = new Date(value);

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

